// import { ReactComponent as EmptyFolderIcon } from "../../assets/dashboard-empty-file.svg";
import { ReactComponent as EmptyFolderIcon } from "../../assets/empty-dashboard.svg";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  alpha,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import { useCallback, useEffect, useRef, useState } from "react";
import OurDictionary from "../../components/OurDictionary/OurDictionary";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { setCrossDomainCookie } from "../../utils/cookie-utils";
import { useLoginPopup } from "../../context/LoginPopupContext";
import { useSnackbar } from "notistack";
import { PageTitle } from "../../pages/CreateBusinessAccount/CreateBusinessAccount";
import { getCookie, getHeaders, getLoginSecured } from "../../utils/api";
import BackdropLoading from "../BackdropLoading/BackdropLoading";
import { navigateToClientPage } from "../../utils/navigation-utils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import envConfig, { buildBusinessDashboardUrl } from "../../config";
import { commonThemes } from "../../utils/themes/themes";
import { getAppTheme } from "../../config/resolve-app-brand";
import { createSessionForExternal } from "../../utils/common";

// Sample data for local development
const SAMPLE_BUSINESS_FILES = [
  {
    xxbID: 1001,
    xxbBusinessName: "Exacc.au",
    xxbAccessStatus: "HasAccess",
    xxsID: 2001,
    xxlFavouriteBF: "Y",
    ssnLastConnnectedDate: "2024-01-15T10:30:00Z",
  },
  {
    xxbID: 1002,
    xxbBusinessName: "Dluga Nazwa Testowa Testowa Testowa Testowa Testowa",
    xxbAccessStatus: "HasAccess",
    xxsID: 2001,
    xxlFavouriteBF: "N",
    ssnLastConnnectedDate: "2024-01-10T14:20:00Z",
  },
  {
    xxbID: 1003,
    xxbBusinessName: "Demo Business ABC",
    xxbAccessStatus: "HasAccess",
    xxsID: 2002,
    xxlFavouriteBF: "N",
    ssnLastConnnectedDate: "2024-01-05T09:15:00Z",
  },
];

// Use environment variable or default to false for production
const USE_SAMPLE_DATA =
  process.env.REACT_APP_USE_SAMPLE_DATA === "true" ||
  window.location.hostname === "localhost";

// Toggle between new and old dashboard layout
// Set to true for new layout with card-based design for <4 files
// Set to false for traditional list-based layout
// This will be determined dynamically based on the route in the component
const USE_NEW_DASHBOARD_DEFAULT = true;

const DEFAULT_SORT_OPTION = "aToZ";
const SORT_OPTIONS = new Set([
  DEFAULT_SORT_OPTION,
  "mostRecent",
  "oldest",
]);

export const normalizeDashboardSortOption = (value) =>
  SORT_OPTIONS.has(value) ? value : DEFAULT_SORT_OPTION;

export const readCurrentUser2FAEnabled = (data) => {
  const value = data?.CurrentUser2FAHasEnabledYN;
  return value === "Y" ? true : value === "N" ? false : null;
};

export const DashboardSortSelect = ({ value, onChange }) => (
  <FormControl
    variant="outlined"
    fullWidth
    sx={{ maxWidth: "240px" }}
    data-testid="dashboard--FormControl-2">
    <InputLabel shrink={true} id="sort-select-label">
      Sort By
    </InputLabel>
    <Select
      labelId="sort-select-label"
      size="small"
      sx={{ maxWidth: "180px" }}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      label="Sort By"
      notched={true}
      data-testid="dashboard--Select-2">
      <MenuItem value={DEFAULT_SORT_OPTION} data-testid="dashboard--MenuItem-6">
        A-Z
      </MenuItem>
      <MenuItem value="mostRecent" data-testid="dashboard--MenuItem-7">
        Most Recent
      </MenuItem>
      <MenuItem value="oldest" data-testid="dashboard--MenuItem-8">
        Oldest
      </MenuItem>
    </Select>
  </FormControl>
);

// const menuItemsMain = [
//   { text: "Open in 2nd instance (s2.exacc.au)", instanceUrl: "s2.exacc.au" },
//   { text: "Open in 3rd instance (s3.exacc.au)", instanceUrl: "s3.exacc.au" },
//   { text: "Open in 4th instance (s4.exacc.au)", instanceUrl: "s4.exacc.au" },
// ];

// const menuItemsUatEnv = [
//   { text: "Open in 2nd instance (s2.exx.au)", instanceUrl: "s2.exx.au" },
//   { text: "Open in 3rd instance (s3.exx.au)", instanceUrl: "s3.exx.au" },
//   { text: "Open in 4th instance (s4.exx.au)", instanceUrl: "s4.exx.au" },
// ];

// const menuItemsTestEnv = [
//   { text: "Open in 2nd instance (s2.olivs.app)", instanceUrl: "s2.olivs.app" },
//   { text: "Open in 3rd instance (s3.olivs.app)", instanceUrl: "s3.olivs.app" },
//   { text: "Open in 4th instance (s4.olivs.app)", instanceUrl: "s4.olivs.app" },
// ];

// const menuItemsStageEnv = [
//   { text: "Open in 2nd instance (s2.ssx.au)", instanceUrl: "s2.ssx.au" },
//   { text: "Open in 3rd instance (s3.ssx.au)", instanceUrl: "s3.ssx.au" },
//   { text: "Open in 4th instance (s4.ssx.au)", instanceUrl: "s4.ssx.au" },
// ];

export const EmptyDashboard = ({ isIncognitoUser, onIncognitoWarning }) => {
  const navigate = useNavigate();

  const { isDarkMode } = useTheme();
  const location = useLocation();
  const theme = getAppTheme(isDarkMode);

  const [loading, setLoading] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "column",
          lg: "row",
        },
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        width: {
          lg: "100%",
          xl: "70%",
        },
        minWidth: {
          lg: "unset",
          xl: "1150px",
        },
        maxWidth: "1382px",
        boxShadow: "0px 0px 10px 0px rgba(69, 90, 100, 0.10)",
        textAlign: "left",
        border: theme.palette.borders.main,
        borderRadius: 1,
        px: 8,
        py: 6,
        my: "140px",
        mx: "auto",
      }}
    >
      <Box
        sx={{
          minWidth: {
            xs: "300px", // Width for extra small screens
            sm: "440px", // Width for small screens
            md: "440px", // Width for medium screens
          },
        }}
      >
        <EmptyFolderIcon width="100%" />
      </Box>
      <Box>
        <Typography
          variant="h3"
          sx={{
            color: theme.palette.text.primary,
            fontSize: "28px",
            mb: 8,
            fontWeight: 300,
            letterSpacing: "-0.176px",
          }}
        >
          Welcome!
        </Typography>
        <Typography
          sx={{
            color: theme.palette.text.primary,
            mb: 6,
            fontSize: "16px",
            fontWeight: 300,
            letterSpacing: "-0.176px",
          }}
        >
          If you are awaiting an invitation to the subscriber's space, please
          contact the owners and make sure invitation was sent to{" "}
          {USE_SAMPLE_DATA ? "your email" : getCookie("loginEmail")}.{" "}
        </Typography>
        <Typography
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 300,
            fontSize: "16px",
            letterSpacing: "-0.176px",
            mb: 6,
          }}
        >
          If you are starting your own Exacc.ly experience, let’s create your
          bussiness account.
        </Typography>
        <Typography
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 300,
            fontSize: "16px",
            letterSpacing: "-0.176px",
            mb: 8,
          }}
        >
          You can enjoy 3 months using Exacc.ly completely free! No payment
          details needed.{" "}
        </Typography>
        <Box
          sx={{
            mt: 5,
            display: "flex",
            alignItems: "center",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "row",
              lg: "row",
            },
            gap: 3,
          }}
        >
          <Button
            onClick={async () => {
              if (isIncognitoUser) {
                onIncognitoWarning();
                return;
              }
              setLoading(true);
              navigate("/create-new-subscriber");

              setLoading(true);
            }}
            disabled={loading}
            sx={{
              width: {
                xs: "100%",
                sm: "100%",
                md: "unset",
              },
            }}
            disableRipple
            type="submit"
            variant="contained"
            color="primary"
            data-testid="dashboard--Button-0">
            Start My Own Exacc Experience
          </Button>
          <Button
            disabled={loading}
            disableRipple
            sx={{
              width: {
                xs: "100%",
                sm: "100%",
                md: "unset",
              },
            }}
            variant="contained"
            color="primary"
            onClick={() => {
              if (isIncognitoUser) {
                onIncognitoWarning();
                return;
              }
              navigate("/create-demo-file", {
                state: { redirectBackTo: location.pathname, isDemo: true },
              });
            }}
            data-testid="dashboard--Button-1">
            New Demo
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const AccessRequestsDashboard = ({
  requestedAccess,
  refetchInvitations,
  fetchBusinessFiles,
}) => {
  const { isDarkMode } = useTheme();
  const theme = getAppTheme(isDarkMode);
  const secondBreakpoint = useMediaQuery("(max-width: 890px)");

  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box>
      <OurDictionary isOpen={isOpen} setIsOpen={setIsOpen} />
      <Box
        sx={{
          p: 4,
          borderRadius: "8px",
          boxShadow: "0px 0px 10px 0px rgba(69, 90, 100, 0.10)",
          border: theme.palette.borders.main,
          borderRadius: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 400,
          }}
        >
          Pending Requests
        </Typography>
        {requestedAccess?.map((request, index) => (
          <>
            <DashboardRequest
              details={{
                request_name: request.RequestName,
                business_id: request.xxbID,
                global_user_id: request.xxuID,
              }}
              refetchInvitations={refetchInvitations}
              fetchBusinessFiles={fetchBusinessFiles}
            />
            {requestedAccess.length !== index + 1 && (
              <Box
                sx={{
                  borderBottom: isDarkMode
                    ? "1px solid var(--divider, #252D3E)"
                    : "1px solid var(--divider, #EDEDED)",
                }}
              />
            )}
          </>
        ))}
      </Box>
    </Box>
  );
};

const InvitationsDashboard = ({
  isOpen,
  setIsOpen,
  invitations,
  refetchInvitations,
  fetchBusinessFiles,
}) => {
  const { isDarkMode } = useTheme();
  const theme = getAppTheme(isDarkMode);
  return (
    <Box>
      <OurDictionary isOpen={isOpen} setIsOpen={setIsOpen} />
      <Box
        sx={{
          p: 4,
          borderRadius: "8px",
          boxShadow: "0px 0px 10px 0px rgba(69, 90, 100, 0.10)",
          border: theme.palette.borders.main,
          borderRadius: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "28px",
            fontWeight: 400,
          }}
        >
          Invitations
        </Typography>
        {invitations?.map((invitation, index) => (
          <>
            <DashboardInvitation
              details={{
                account_type:
                  invitation.userOrSubscriberInvitationType_US === "U"
                    ? "business"
                    : "subscriber",
                invitation_name: invitation.Name,
                sent_by: invitation.userInvitedByFullName,
                id: invitation.Id,
              }}
              refetchInvitations={refetchInvitations}
              fetchBusinessFiles={fetchBusinessFiles}
            />
            {invitations.length !== index + 1 && (
              <Box
                sx={{
                  borderBottom: isDarkMode
                    ? "1px solid var(--divider, #252D3E)"
                    : "1px solid var(--divider, #EDEDED)",
                }}
              />
            )}
          </>
        ))}
      </Box>
    </Box>
  );
};

const DashboardRequest = ({
  details,
  refetchInvitations,
  fetchBusinessFiles,
}) => {
  const secondBreakpoint = useMediaQuery("(max-width: 890px)");
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const { isDarkMode } = useTheme();
  const { handleOpen: realHandleOpen } = useLoginPopup();
  const handleOpen = USE_SAMPLE_DATA ? () => {} : realHandleOpen;
  const theme = getAppTheme(isDarkMode);

  const handleAcceptOrDeny = async (optionSelected) => {
    const url = `https://${envConfig.apiDev1}/api/v3/en-au/databases/access-request-confirmation-by-user?BaseHostURL=${envConfig.loginUrl}`;
    const headers = getHeaders();
    const body = {
      BusinessFileId_xxbID: details.business_id,
      GlobalUserId_xxuID: details.global_user_id,
      optionSelected: optionSelected,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      if (result?.SuccessYN === "Y") {
        enqueueSnackbar(
          optionSelected === 1
            ? "Invitation accepted successfully"
            : "Permissions managed successfully",
          { variant: "success" }
        );
        refetchInvitations();
        await fetchBusinessFiles();
      }
    } catch (error) {
      handleOpen();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (optionSelected) => {
    handleAcceptOrDeny(optionSelected);
    handleClose();
  };

  return (
    <Box sx={{ py: 2, display: "flex", justifyContent: "space-between" }}>
      <Typography
        sx={{
          mt: 0,
          mb: 1,
          fontSize: "20px",
          fontWeight: 400,
          lineHeight: "166%",
          letterSpacing: "0.4px",
        }}
      >
        {details.request_name}
      </Typography>
      <Box
        sx={{
          textAlign: secondBreakpoint ? "right" : "unset",
          mt: secondBreakpoint ? 1 : 0,
        }}
      >
        <Button
          sx={{
            background: "transparent",
            mr: "9px",
          }}
          disableRipple
          variant="outlined"
          onClick={() => handleAcceptOrDeny(3)}
          data-testid="dashboard--Button-2">
          Decline
        </Button>
        <Button
          onClick={handleClick}
          disableRipple
          variant="contained"
          color="primary"
          endIcon={<ExpandMoreIcon />}
          data-testid="dashboard--Button-3">
          Accept
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem
            onClick={() => handleMenuItemClick(1)}
            data-testid="dashboard--MenuItem-0">
            Accept with my permissions
          </MenuItem>
          <MenuItem
            onClick={() => handleMenuItemClick(2)}
            data-testid="dashboard--MenuItem-1">
            Accept and manage permissions
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export const checkAndUpdateApiKeyExpiration = () => {
  const apikeyexpirationdate = getCookie("apiKeyExpirationUTCDate");

  const expirationDate = new Date(apikeyexpirationdate + "Z");
  const now = new Date();

  const timeDifference = expirationDate.getTime() - now.getTime();
  // const fifteenMinutes = 15 * 60 * 1000;
  const prolongByShort = envConfig.loginUrl.includes(".app")
    ? 2 * 60 * 1000
    : 15 * 60 * 1000;

  const expiryPeriodLong = 3 * 24 * 60 * 60 * 1000; // 3 days

  let newExpirationDate;
  if (timeDifference <= prolongByShort) {
    newExpirationDate = new Date(now.getTime() + prolongByShort);
  } else {
    newExpirationDate = new Date(now.getTime() + expiryPeriodLong);
  }

  setCrossDomainCookie(
    "apiKeyExpirationUTCDate",
    newExpirationDate.toISOString().split(".")[0]
  );
};

const DashboardInvitation = ({
  details,
  refetchInvitations,
  fetchBusinessFiles,
}) => {
  const secondBreakpoint = useMediaQuery("(max-width: 890px)");
  const { enqueueSnackbar } = useSnackbar();
  const { handleOpen: realHandleOpen } = useLoginPopup();
  const handleOpen = USE_SAMPLE_DATA ? () => {} : realHandleOpen;

  const { isDarkMode } = useTheme();
  const theme = getAppTheme(isDarkMode);

  const handleAcceptOrDeny = async (isAccept) => {
    const url =
      details.account_type === "subscriber"
        ? `https://${envConfig.apiDev1}/api/v3/en-au/databases/accept-or-deny-subscriber-pending-invitation?BaseHostURL=${envConfig.loginUrl}`
        : `https://${envConfig.apiDev1}/api/v4/en-au/databases/accept-or-deny-business-file-pending-invitation-async?BaseHostURL=${envConfig.loginUrl}`;
    const body =
      details.account_type === "subscriber"
        ? {
            AcceptAOrDenyN: isAccept ? "A" : "N",
            SubscriberId: details.id,
          }
        : { AcceptAOrDenyD: isAccept ? "A" : "D", DatabaseId: details.id };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API response data:", result);
      if (result?.SuccessYN === "Y") {
        enqueueSnackbar(
          isAccept
            ? "Invitation accepted successfully"
            : "Invitation declined successfully",
          { variant: "success" }
        );
        checkAndUpdateApiKeyExpiration();
        refetchInvitations();
        await fetchBusinessFiles();
      }
    } catch (error) {
      handleOpen();
    }
  };

  return (
    <Box sx={{ py: 3 }}>
      <Typography
        sx={{
          color: theme.typography.linkColor,
          fontSize: "13px",
          fontWeight: 400,
          lineHeight: "266%",
          letterSpacing: "1px",
          textTransform: "uppercase",
          mt: 0,
          mb: 2,
        }}
      >
        {details.account_type === "subscriber"
          ? "SUBSCRIBER ACCOUNT INVITATION"
          : "BUSINESS ACCOUNT INVITATION"}
      </Typography>
      <Typography
        sx={{
          mt: 0,
          mb: 1.5,
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "166%",
          letterSpacing: "0.4px",
        }}
      >
        {details.account_type === "subscriber"
          ? "You’ve been invited to join Subscriber Account. This membership will allow you to access and manage businesses linked to this account."
          : `You’ve been invited to join Business Account. You’ll get access to your business account shared with ${details.invitation_name} Subscriber Account.`}
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: secondBreakpoint ? "column" : "row",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: secondBreakpoint ? "column" : "row",
            alignItems: secondBreakpoint ? "flex-start" : "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.main,
              fontSize: "18px",
              fontWeight: 500,
              lineHeight: "28px",
              letterSpacing: "0.15px",
              my: 0,
              mr: 2,
            }}
          >
            {details.invitation_name}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.text.disabled,
              fontSize: "15px",
              fontWeight: 400,
              lineHeight: "24px",
              letterSpacing: "0.15px",
            }}
          >
            {`Invitation sent by ${details.sent_by}`}
          </Typography>
        </Box>
        <Box
          sx={{
            textAlign: secondBreakpoint ? "right" : "unset",
            mt: secondBreakpoint ? 1.5 : 0,
          }}
        >
          <Button
            sx={{
              background: "transparent",
              mr: "9px",
            }}
            disableRipple
            variant="outlined"
            onClick={() => handleAcceptOrDeny(false)}
            data-testid="dashboard--Button-4">
            Decline
          </Button>
          <Button
            onClick={() => handleAcceptOrDeny(true)}
            disableRipple
            variant="contained"
            color="primary"
            data-testid="dashboard--Button-5">
            Accept
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const BusinessFileCard = ({
  business,
  handleDbSelect,
  handleContextMenu,
  handleFavoriteToggle,
  theme,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        px: { xs: 2, sm: 2, md: 3, lg: 4 },
        py: { xs: 1.5, sm: 1.5, md: 2, lg: 2.5 },
        borderRadius: "10px",
        boxShadow: "0px 0px 40px 0px rgba(73, 86, 112, 0.1)",
        backgroundColor: theme.palette.background.paper,
        border: theme.palette.borders.main === "none" ? "1px solid transparent" : theme.palette.borders.main,
        cursor: "pointer",
        transition: "all 0.3s ease",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          border: `1px solid ${theme.palette.primary.main}`,
        },
      }}
      onClick={() =>
        handleDbSelect(business.xxbID, business.xxbAccessStatus, business.xxsID)
      }
      onContextMenu={(event) => {
        event.preventDefault();
        handleContextMenu(event, business);
      }}
      data-testid={`business-file-card-${business.xxbID}`}>
      <IconButton
        aria-label="more"
        aria-controls={`business-menu-${business.xxbID}`}
        aria-haspopup="true"
        onClick={(event) => {
          event.stopPropagation();
          handleContextMenu(event, business);
        }}
        sx={{
          ml: 1,
        }}
        data-testid="dashboard--IconButton-0">
        <MoreVertIcon />
      </IconButton>
      <Box
        position="relative"
        width={90}
        height={90}
        display={{ xs: "none", sm: "flex" }}
        sx={{ flexShrink: 0, mr: 3 }}
      >
        <Box
          className="icon-hexagon"
          sx={{
            color:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.primary.main, 0.3)
                : theme.palette.primary.light,
            position: "relative",
            transition: "color 0.3s ease",
          }}
        >
          <svg
            width="90"
            height="90"
            viewBox="0 0 69 76"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "100%" }}
          >
            <path
              d="M29.5 1.88675C32.594 0.100423 36.406 0.100423 39.5 1.88675L63.275 15.6132C66.369 17.3996 68.275 20.7008 68.275 24.2735V51.7265C68.275 55.2992 66.369 58.6004 63.275 60.3868L39.5 74.1132C36.406 75.8996 32.594 75.8996 29.5 74.1132L5.72501 60.3868C2.631 58.6004 0.72501 55.2992 0.72501 51.7265V24.2735C0.72501 20.7008 2.631 17.3996 5.72501 15.6132L29.5 1.88675Z"
              fill="currentColor"
            />
          </svg>
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="58"
            height="58"
            viewBox="0 0 80 80"
            fill="none"
          >
            <path
              d="M52.7949 23.9123V21.2777C52.7949 17.5889 52.7949 15.7445 52.077 14.3356C51.4456 13.0962 50.438 12.0886 49.1986 11.4571C47.7897 10.7393 45.9453 10.7393 42.2565 10.7393H36.9872C33.2984 10.7393 31.454 10.7393 30.0451 11.4571C28.8057 12.0886 27.7981 13.0962 27.1667 14.3356C26.4488 15.7445 26.4488 17.5889 26.4488 21.2777V23.9123M10.072 33.8789C11.2594 35.0169 24.0964 46.9652 39.6218 46.9652C55.1472 46.9652 67.9843 35.0169 69.1717 33.8789M10.072 33.8789C9.98242 35.4159 9.98242 37.3043 9.98242 39.72V54.2104C9.98242 59.7436 9.98242 62.5102 11.0593 64.6236C12.0065 66.4826 13.5179 67.9941 15.3769 68.9413C17.4903 70.0181 20.2569 70.0181 25.7901 70.0181H53.4536C58.9868 70.0181 61.7534 70.0181 63.8668 68.9413C65.7258 67.9941 67.2372 66.4826 68.1844 64.6236C69.2613 62.5102 69.2613 59.7436 69.2613 54.2104V39.72C69.2613 37.3043 69.2613 35.4159 69.1717 33.8789M10.072 33.8789C10.1877 31.8956 10.4526 30.4975 11.0593 29.3068C12.0065 27.4478 13.5179 25.9364 15.3769 24.9892C17.4903 23.9123 20.2569 23.9123 25.7901 23.9123H53.4536C58.9868 23.9123 61.7534 23.9123 63.8668 24.9892C65.7258 25.9364 67.2372 27.4478 68.1844 29.3068C68.7911 30.4975 69.056 31.8956 69.1717 33.8789"
              stroke={theme.palette.primary.main}
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: {
              xs: "16px",
              md: "22px",
              lg: "22px",
              xl: "24px",
            },
            fontWeight: 300,
            fontStyle: "normal",
            color: theme.palette.primary.main,
            wordBreak: "break-word",
            lineHeight: 1.2,
          }}
        >
          {business.xxbBusinessName}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "12px", lg: "14px" },
            fontWeight: 400,
            fontStyle: "normal",
            color: theme.palette.text.secondary,
          }}
        >
          {business.xxbAccessStatus === "HasAccess"
            ? "Owner Access"
            : business.xxbAccessStatus}
        </Typography>
      </Box>
      {/* <IconButton
        aria-label="toggle favorite"
        onClick={(event) => {
          event.stopPropagation();
          handleFavoriteToggle(
            business,
            business.xxlFavouriteBF === "Y" ? "N" : "Y"
          );
        }}
        sx={{
          ml: 1,
          color:
            business.xxlFavouriteBF === "Y"
              ? theme.palette.primary.main
              : theme.palette.action.disabled,
        }}
      >
        {business.xxlFavouriteBF === "Y" ? (
          <FavoriteIcon />
        ) : (
          <FavoriteBorderIcon />
        )}
      </IconButton> */}
    </Box>
  );
};

const BusinessFilesDashboard = ({
  files,
  fullwidth,
  handleDbSelect,
  subscriberTextInfo,
  handleFavChanged,
  setOpenSendRequestAccess,
  setOpenRequestInProgress,
  requestAccessToBusinessFile,
  setCurrentSelectedDatabase,
  setCurrentSelectedSubscriber,
  isIncognitoUser,
  onIncognitoWarning,
}) => {
  const location = useLocation();
  const { handleOpen: realHandleOpen } = useLoginPopup();
  const handleOpen = USE_SAMPLE_DATA ? () => {} : realHandleOpen;
  const firstBreakpoint = useMediaQuery("(max-width: 1100px)");
  const secondBreakpoint = useMediaQuery("(max-width: 890px)");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  // Determine whether to use new dashboard based on route
  // Always use new dashboard on /user-dashboard route
  // Also use it on / route for localhost development
  // Show tile view when there are 7 or fewer business files
  const USE_NEW_DASHBOARD = USE_NEW_DASHBOARD_DEFAULT && files?.length <= 7;
  // TODO: Done based on the https://app.ssx.au/24960/todo/task/822 ticket.
  // Temporarily keep dashboard sorting fixed to A-Z while the sort selector is hidden.
  // const [sortOption, setSortOption] = useState(() => {
  //   const savedSort = USE_SAMPLE_DATA ? "" : getCookie("sortBy");
  //   return normalizeDashboardSortOption(savedSort);
  // });
  const sortOption = DEFAULT_SORT_OPTION;
  const [statusFilter, setStatusFilter] = useState("Active");

  const { isDarkMode } = useTheme();
  const theme = getAppTheme(isDarkMode);
  const { enqueueSnackbar } = useSnackbar();

  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleContextMenu = (event, business) => {
    event.stopPropagation();
    event.preventDefault();
    setSelectedBusiness(business);
    setCurrentSelectedDatabase(business.xxbID);
    setCurrentSelectedSubscriber(business.xxsID);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = async (instanceUrl) => {
    if (selectedBusiness) {
      if (USE_SAMPLE_DATA) {
        // For sample data, just show a message
        enqueueSnackbar(
          `Would open business ${selectedBusiness.xxbBusinessName} on ${instanceUrl}`,
          { variant: "info" }
        );
        handleClose();
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "SendRequest") {
        setOpenSendRequestAccess(true);
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "RequestInProgress") {
        setOpenRequestInProgress(true);
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "AutoAdd") {
        setCrossDomainCookie("databaseId", selectedBusiness.xxbID);
        // TODO: double check if this is the correct subscriberId
        const result = await requestAccessToBusinessFile({
          dbId: selectedBusiness.xxbID,
          subscriberId: selectedBusiness.xxsID,
        });
        console.log(result);
        if (
          result.SuccessYN === "Y" ||
          result.successYN === "Y" ||
          result.SuccesYN === "Y"
        ) {
          const dashboardUrl = buildBusinessDashboardUrl(selectedBusiness.xxbID);
          if (!dashboardUrl) {
            handleClose();
            return;
          }
          const result = await createSessionForExternal(dashboardUrl);
          console.log(result);
          if (!result) {
            handleOpen();
          }
        }
        return;
      }

      setCrossDomainCookie("databaseId", selectedBusiness.xxbID);
      const dashboardUrl = buildBusinessDashboardUrl(
        selectedBusiness.xxbID,
        instanceUrl
      );
      if (!dashboardUrl) {
        handleClose();
        return;
      }
      const result = await createSessionForExternal(dashboardUrl);
      console.log(result);
      if (!result) {
        handleOpen();
      }
    }
    handleClose();
  };

  const handleOpenInNewTab = async () => {
    if (selectedBusiness) {
      if (USE_SAMPLE_DATA) {
        enqueueSnackbar(
          `Would open business ${selectedBusiness.xxbBusinessName} in new tab`,
          { variant: "info" }
        );
        handleClose();
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "SendRequest") {
        setOpenSendRequestAccess(true);
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "RequestInProgress") {
        setOpenRequestInProgress(true);
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "AutoAdd") {
        setCrossDomainCookie("databaseId", selectedBusiness.xxbID);
        const result = await requestAccessToBusinessFile({
          dbId: selectedBusiness.xxbID,
          subscriberId: selectedBusiness.xxsID,
        });
        console.log(result);
        if (
          result.SuccessYN === "Y" ||
          result.successYN === "Y" ||
          result.SuccesYN === "Y"
        ) {
          window.open(buildBusinessDashboardUrl(selectedBusiness.xxbID), "_blank");
        }
        handleClose();
        return;
      }

      setCrossDomainCookie("databaseId", selectedBusiness.xxbID);
      window.open(buildBusinessDashboardUrl(selectedBusiness.xxbID), "_blank");
    }
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedBusiness(null);
    setCurrentSelectedDatabase(null);
    setCurrentSelectedSubscriber(null);
  };

  const filteredAndSortedBusinesses = () => {
    console.log("=== SORTING DEBUG START ===");
    console.log("Sort option:", sortOption);
    console.log("Status filter:", statusFilter);

    let filteredBusinesses =
      files?.filter((business) => {
        // Apply status filter
        if (
          statusFilter === "Active" &&
          business.xxbAccessStatus !== "HasAccess"
        ) {
          return false;
        }
        // "Active + Available" shows all files, so no additional filtering needed

        // Apply search filter
        return (
          business.xxbBusinessName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          business.xxbID.toString().includes(searchTerm)
        );
      }) || [];

    // Group by dates to understand the data
    const dateGroups = {};
    filteredBusinesses.forEach((b) => {
      const date = b.ssnLastConnnectedDate;
      if (!dateGroups[date]) dateGroups[date] = [];
      dateGroups[date].push(b.xxbBusinessName);
    });

    console.log("Date groups in data:");
    Object.entries(dateGroups).forEach(([date, names]) => {
      console.log(
        `  ${date}: ${names.length} items (e.g., ${names
          .slice(0, 3)
          .join(", ")})`
      );
    });

    // Sort by selected option first
    if (sortOption === "mostRecent") {
      console.log("Sorting by MOST RECENT");
      filteredBusinesses.sort((a, b) => {
        const dateA = a.ssnLastConnnectedDate;
        const dateB = b.ssnLastConnnectedDate;

        // Sort by date descending (most recent first)
        const diff = new Date(dateB) - new Date(dateA);
        // If dates are same, sort by name for consistency
        if (diff === 0) {
          return a.xxbBusinessName.localeCompare(b.xxbBusinessName);
        }
        return diff;
      });
    } else if (sortOption === "oldest") {
      console.log("Sorting by OLDEST");
      filteredBusinesses.sort((a, b) => {
        const dateA = a.ssnLastConnnectedDate;
        const dateB = b.ssnLastConnnectedDate;

        // Sort by date ascending (oldest first)
        const diff = new Date(dateA) - new Date(dateB);
        // If dates are same, sort by name for consistency
        if (diff === 0) {
          return a.xxbBusinessName.localeCompare(b.xxbBusinessName);
        }
        return diff;
      });
    } else {
      console.log("Sorting by A-Z");
      // Default A-Z sorting
      filteredBusinesses.sort((a, b) =>
        a.xxbBusinessName.localeCompare(b.xxbBusinessName)
      );
    }

    // Log results after primary sort
    const afterPrimarySort = filteredBusinesses.map((b) => ({
      name: b.xxbBusinessName.substring(0, 30),
      date: b.ssnLastConnnectedDate, // Check BOTH spellings!
      fav: b.xxlFavouriteBF,
    }));
    console.log("After primary sorting:", afterPrimarySort);

    // Only sort by favorites if we're using the new dashboard (7 or fewer files)
    // When there are more than 7 files, favorites are shown in a separate panel
    if (USE_NEW_DASHBOARD) {
      console.log("Sorting favorites to top (tile view with ≤7 files)");
      filteredBusinesses.sort((a, b) => {
        if (a.xxlFavouriteBF === "Y" && b.xxlFavouriteBF !== "Y") return -1;
        if (a.xxlFavouriteBF !== "Y" && b.xxlFavouriteBF === "Y") return 1;
        return 0;
      });
    } else {
      console.log(
        "Not sorting favorites (list view with >7 files - separate favorites panel)"
      );
    }

    // Log final results
    const finalResults = filteredBusinesses.map((b) => ({
      name: b.xxbBusinessName.substring(0, 30),
      date: b.ssnLastConnnectedDate, // Check BOTH spellings!
      fav: b.xxlFavouriteBF === "Y" ? "★" : "",
    }));
    console.log("Final order:", finalResults);
    console.log("=== SORTING DEBUG END ===");

    return filteredBusinesses;
  };

  const handleFavoriteToggle = async (business, newValue) => {
    if (USE_SAMPLE_DATA) {
      // For sample data, just show a message
      enqueueSnackbar(
        newValue === "Y" ? "Added to favorites" : "Removed from favorites",
        { variant: "success" }
      );
      handleClose();
      // In a real implementation, you might want to update local state here
      await handleFavChanged();
      return;
    }

    const favoriteData = {
      DatabaseId: business.xxbID,
      FavouriteYN: newValue,
    };
    const url = `https://${envConfig.apiDev1}/api/v3/en-au/databases/manage-user-favourite-databases?BaseHostURL=${envConfig.loginUrl}`;
    const response = await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(favoriteData),
    });
    const responseData = await response.json();
    // Error handled by interceptor
    if (responseData.SuccesYN === "Y") {
      await handleFavChanged();
    }
    handleClose();
  };

  return (
    <Container
      sx={{
        marginTop: 5,
        p: 1,
        pb: 3,
        position: "relative",
        boxShadow: "0px 0px 10px 0px rgba(69, 90, 100, 0.10)",
        border: theme.palette.borders.main,
        borderRadius: 1,
        backgroundColor: isDarkMode ? theme.palette.background.paper : "#FDFDFD",
      }}
    >
      {!USE_NEW_DASHBOARD && (
        <Box
          sx={{
            position: "absolute",
            top: "24px",
            width: secondBreakpoint ? "206px" : "unset",
            right: fullwidth
              ? "24px"
              : secondBreakpoint
              ? 0
              : firstBreakpoint
              ? "unset"
              : "32px",
            textAlign: secondBreakpoint ? "right" : "unset",
            width: secondBreakpoint ? "206px" : "unset",
          }}
        >
          <Button
            sx={{
              mr: secondBreakpoint ? 0 : 2,
              mb: secondBreakpoint ? 1 : 0,
            }}
            disableRipple
            variant="outlined"
            onClick={() => {
              if (isIncognitoUser) {
                onIncognitoWarning();
                return;
              }
              navigate("/create-demo-file", {
                state: { redirectBackTo: location.pathname, isDemo: true },
              });
            }}
            data-testid="dashboard--Button-6">
            New Demo
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{
              width: secondBreakpoint ? "190px" : "unset",
              justifyContent: secondBreakpoint ? "center" : "unset",
            }}
            onClick={() => {
              if (isIncognitoUser) {
                onIncognitoWarning();
                return;
              }
              navigate("/create-new-exacc-file", {
                state: { redirectBackTo: location.pathname },
              });
            }}
            data-testid="dashboard--Button-7">
            {`+   Add New Business`}
          </Button>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: { xs: "space-evenly", sm: "space-between" },
          alignItems: { xs: "flex-start", sm: "center" },
          mt: !USE_NEW_DASHBOARD
            ? secondBreakpoint
              ? "100px"
              : firstBreakpoint
              ? "64px"
              : "24px"
            : { xs: "12px", sm: "24px" },
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "16px", sm: "20px", md: "24px" },
            fontWeight: 400,
            lineHeight: "150%",
            letterSpacing: "-0.264px",
          }}
        >
          {USE_NEW_DASHBOARD ? "Your Business Files" : "Select business file"}
        </Typography>
        {USE_NEW_DASHBOARD && (
          <Box sx={{ display: "flex", gap: 2, mt: { xs: 2, sm: 0 }, mb: { xs: 1, sm: 0 } }}>
            {files?.length >= 6 && (
              <TextField
                sx={{
                  width: { xs: "180px", sm: "240px" },
                }}
                size="small"
                placeholder="Search business files"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                data-testid="dashboard--TextField-0" />
            )}
            <FormControl
              variant="outlined"
              size="small"
              sx={{
                minWidth: "120px",
              }}
              data-testid="dashboard--FormControl-0">
              <InputLabel shrink={true} id="status-select-label-new">
                Access
              </InputLabel>
              <Select
                labelId="status-select-label-new"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Access"
                notched={true}
                data-testid="dashboard--Select-0">
                <MenuItem value="Active" data-testid="dashboard--MenuItem-2">Active</MenuItem>
                <MenuItem value="Active + Available" data-testid="dashboard--MenuItem-3">Active + Available</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>
      {!USE_NEW_DASHBOARD && (
        <Box sx={{ display: "flex" }}>
          <TextField
            sx={{
              maxWidth: "240px",
              mr: 2,
            }}
            size="small"
            fullWidth
            label="Search"
            variant="outlined"
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="dashboard--TextField-1" />
          <FormControl
            variant="outlined"
            fullWidth
            sx={{
              maxWidth: "240px",
            }}
            data-testid="dashboard--FormControl-1">
            <InputLabel shrink={true} id="status-select-label">
              Access
            </InputLabel>
            <Select
              labelId="status-select-label"
              size="small"
              sx={{ maxWidth: "180px" }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Access"
              notched={true}
              data-testid="dashboard--Select-1">
              <MenuItem value="Active" data-testid="dashboard--MenuItem-4">Active</MenuItem>
              <MenuItem value="Active + Available" data-testid="dashboard--MenuItem-5">Active + Available</MenuItem>
            </Select>
          </FormControl>
          {/* TODO: Done based on the https://app.ssx.au/24960/todo/task/822 ticket. */}
          {/* Temporarily hidden while dashboard sorting is fixed to A-Z.
          <DashboardSortSelect
            value={sortOption}
            onChange={(selectedValue) => {
              setSortOption(selectedValue);
              if (!USE_SAMPLE_DATA) {
                setCrossDomainCookie("sortBy", selectedValue);
              }
            }}
          /> */}
        </Box>
      )}
      {USE_NEW_DASHBOARD ? (
        <>
          <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mt: 1 }}>
            <Grid item xs={6} sm={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  px: { xs: 2, sm: 2, md: 2, lg: 4 },
                  py: { xs: 1, sm: 1, md: 1, lg: 2 },
                  borderRadius: "10px",
                  boxShadow: "0px 0px 40px 0px rgba(73, 86, 112, 0.1)",
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid transparent`,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    border: `1px solid ${theme.palette.primary.main}`,
                  },
                }}
                onClick={() => {
                  if (isIncognitoUser) {
                    onIncognitoWarning();
                    return;
                  }
                  navigate("/create-new-exacc-file", {
                    state: { redirectBackTo: location.pathname },
                  });
                }}
                data-testid="dashboard--Box-1">
                <Box
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mr: 3,
                    position: "relative",
                  }}
                  display={{ xs: "none", sm: "flex" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="66"
                    height="66"
                    viewBox="0 0 76 84"
                    fill="none"
                  >
                    <path
                      d="M33 1.88675C36.094 0.100424 39.906 0.100424 43 1.88675L70.2391 17.6132C73.3331 19.3996 75.2391 22.7008 75.2391 26.2735V57.7265C75.2391 61.2992 73.3331 64.6004 70.2391 66.3868L43 82.1132C39.906 83.8996 36.094 83.8996 33 82.1132L5.76091 66.3868C2.6669 64.6004 0.760906 61.2992 0.760906 57.7265V26.2735C0.760906 22.7008 2.6669 19.3996 5.76091 17.6132L33 1.88675Z"
                      fill={isDarkMode ? "#252D3E" : "#EDEDED"}
                    />
                  </svg>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -47%)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="41"
                      height="41"
                      viewBox="0 0 51 51"
                      fill="none"
                    >
                      <path
                        d="M17.1783 25.5511H33.7168M25.4476 17.2818V33.8203M44.0533 25.5511C44.0533 35.8268 35.7232 44.1568 25.4476 44.1568C15.1719 44.1568 6.8418 35.8268 6.8418 25.5511C6.8418 15.2754 15.1719 6.94531 25.4476 6.94531C35.7232 6.94531 44.0533 15.2754 44.0533 25.5511Z"
                        stroke="#6D7B96"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </Box>
                </Box>
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "16px",
                        md: "18px",
                        lg: "20px",
                      },
                      fontWeight: 300,
                      fontStyle: "normal",
                      color: theme.palette.text.secondary,
                      flexGrow: 1,
                    }}
                  >
                    Add New Business
                  </Typography>
              </Box>
            </Grid>

            <Grid item xs={6} sm={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  px: { xs: 2, sm: 2, md: 2, lg: 4 },
                  py: { xs: 1, sm: 1, md: 1, lg: 2 },
                  borderRadius: "10px",
                  boxShadow: "0px 0px 40px 0px rgba(73, 86, 112, 0.1)",
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid transparent`,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    border: `1px solid ${theme.palette.primary.main}`,
                  },
                }}
                onClick={() => {
                  if (isIncognitoUser) {
                    onIncognitoWarning();
                    return;
                  }
                  navigate("/create-demo-file", {
                    state: { redirectBackTo: location.pathname, isDemo: true },
                  });
                }}
                data-testid="dashboard--Box-2">
                <Box
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mr: 3,
                    position: "relative",
                  }}
                  display={{ xs: "none", sm: "flex" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="66"
                    height="66"
                    viewBox="0 0 76 84"
                    fill="none"
                  >
                    <path
                      d="M33 1.88675C36.094 0.100424 39.906 0.100424 43 1.88675L70.2391 17.6132C73.3331 19.3996 75.2391 22.7008 75.2391 26.2735V57.7265C75.2391 61.2992 73.3331 64.6004 70.2391 66.3868L43 82.1132C39.906 83.8996 36.094 83.8996 33 82.1132L5.76091 66.3868C2.6669 64.6004 0.760906 61.2992 0.760906 57.7265V26.2735C0.760906 22.7008 2.6669 19.3996 5.76091 17.6132L33 1.88675Z"
                      fill={isDarkMode ? "#252D3E" : "#EDEDED"}
                    />
                  </svg>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -46%)",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="41"
                      height="41"
                      viewBox="0 0 51 51"
                      fill="none"
                    >
                      <path
                        d="M15.111 43.9068L25.4476 35.6376L35.7841 43.9068M16.7649 35.6376H34.1303C37.6037 35.6376 39.3404 35.6376 40.667 34.9616C41.834 34.367 42.7828 33.4183 43.3774 32.2513C44.0533 30.9246 44.0533 29.1879 44.0533 25.7145V16.6184C44.0533 13.145 44.0533 11.4083 43.3774 10.0816C42.7828 8.91465 41.834 7.96588 40.667 7.37128C39.3404 6.69531 37.6037 6.69531 34.1303 6.69531H16.7649C13.2915 6.69531 11.5548 6.69531 10.2281 7.37128C9.06114 7.96588 8.11237 8.91465 7.51777 10.0816C6.8418 11.4083 6.8418 13.145 6.8418 16.6184V25.7145C6.8418 29.1879 6.8418 30.9246 7.51777 32.2513C8.11237 33.4183 9.06114 34.367 10.2281 34.9616C11.5548 35.6376 13.2915 35.6376 16.7649 35.6376Z"
                        stroke="#6D7B96"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </Box>
                </Box>
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "16px",
                        md: "18px",
                        lg: "20px",
                      },
                      fontWeight: 300,
                      fontStyle: "normal",
                      color: theme.palette.text.secondary,
                      flexGrow: 1,
                    }}
                  >
                    New Demo
                  </Typography>
              </Box>
            </Grid>

            {filteredAndSortedBusinesses().map((business, index) => (
              <Grid item key={index} xs={12} lg={6}>
                <BusinessFileCard
                  business={business}
                  handleDbSelect={handleDbSelect}
                  handleContextMenu={handleContextMenu}
                  handleFavoriteToggle={handleFavoriteToggle}
                  theme={theme}
                />
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <Grid
          sx={{ mt: 1, display: "flex", flexDirection: "column" }}
          container
          spacing="12px"
        >
          <div>
            {filteredAndSortedBusinesses().map((business, index) => (
              <Grid
                item
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  paddingLeft: "12px",
                  "&:hover": {
                    color: theme.palette.primary.main,
                  },
                }}
                onClick={() =>
                  handleDbSelect(
                    business.xxbID,
                    business.xxbAccessStatus,
                    business.xxsID
                  )
                }
                onContextMenu={(event) => {
                  event.preventDefault();
                  handleContextMenu(event, business);
                }}
                data-testid={`business-file-list-item-${business.xxbID}`}
              >
                <IconButton
                  aria-label="more"
                  aria-controls={`long-menu-${index}`}
                  aria-haspopup="true"
                  onClick={(event) => handleContextMenu(event, business)}
                  data-testid="dashboard--IconButton-1">
                  <MoreVertIcon />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    mx: 2,
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  {business.xxbBusinessName}
                </Typography>
              </Grid>
            ))}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() =>
                  handleFavoriteToggle(
                    selectedBusiness,
                    selectedBusiness.xxlFavouriteBF === "Y" ? "N" : "Y"
                  )
                }
                data-testid="dashboard--MenuItem-9">
                {selectedBusiness?.xxlFavouriteBF === "Y"
                  ? "Remove from favorites"
                  : "Add to favorites"}
              </MenuItem>
              <MenuItem onClick={handleOpenInNewTab} data-testid="dashboard--MenuItem-10">
                <OpenInNewIcon sx={{ mr: 1, fontSize: 20 }} />
                Open in new tab
              </MenuItem>
            </Menu>
          </div>
        </Grid>
      )}
    </Container>
  );
};

const FavoritesDashboard = ({
  favorites = [],
  fullwidth,
  handleDbSelect,
  handleFavChanged,
  setOpenSendRequestAccess,
  setOpenRequestInProgress,
  requestAccessToBusinessFile,
  setCurrentSelectedDatabase,
  setCurrentSelectedSubscriber,
}) => {
  const { handleOpen: realHandleOpen } = useLoginPopup();
  const handleOpen = USE_SAMPLE_DATA ? () => {} : realHandleOpen;
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const { isDarkMode } = useTheme();
  const theme = getAppTheme(isDarkMode);
  const { enqueueSnackbar } = useSnackbar();

  // Get sort option from cookie
  const sortOption = USE_SAMPLE_DATA ? "" : getCookie("sortBy") || "";

  // Sort favorites using same logic as main list
  const sortedFavorites = [...favorites].sort((a, b) => {
    if (sortOption === "mostRecent") {
      const dateA = a.ssnLastConnnectedDate;
      const dateB = b.ssnLastConnnectedDate;
      const diff = new Date(dateB) - new Date(dateA);
      if (diff === 0) {
        return a.xxbBusinessName.localeCompare(b.xxbBusinessName);
      }
      return diff;
    } else if (sortOption === "oldest") {
      const dateA = a.ssnLastConnnectedDate;
      const dateB = b.ssnLastConnnectedDate;
      const diff = new Date(dateA) - new Date(dateB);
      if (diff === 0) {
        return a.xxbBusinessName.localeCompare(b.xxbBusinessName);
      }
      return diff;
    } else {
      // Default A-Z sorting
      return a.xxbBusinessName.localeCompare(b.xxbBusinessName);
    }
  });

  const handleContextMenu = (event, business) => {
    event.preventDefault();
    setSelectedBusiness(business);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = async (instanceUrl) => {
    if (selectedBusiness) {
      if (USE_SAMPLE_DATA) {
        // For sample data, just show a message
        enqueueSnackbar(
          `Would open business ${selectedBusiness.xxbBusinessName} on ${instanceUrl}`,
          { variant: "info" }
        );
        handleClose();
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "SendRequest") {
        setOpenSendRequestAccess(true);
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "RequestInProgress") {
        setOpenRequestInProgress(true);
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "AutoAdd") {
        setCrossDomainCookie("databaseId", selectedBusiness.xxbID);
        // TODO: double check if this is the correct subscriberId
        const result = await requestAccessToBusinessFile({
          dbId: selectedBusiness.xxbID,
          subscriberId: selectedBusiness.xxsID,
        });
        console.log(result);
        if (
          result.SuccessYN === "Y" ||
          result.successYN === "Y" ||
          result.SuccesYN === "Y"
        ) {
          const dashboardUrl = buildBusinessDashboardUrl(selectedBusiness.xxbID);
          if (!dashboardUrl) {
            handleClose();
            return;
          }
          const result = await createSessionForExternal(dashboardUrl);
          console.log(result);
          if (!result) {
            handleOpen();
          }
        }
        return;
      }

      setCrossDomainCookie("databaseId", selectedBusiness.xxbID);
      const dashboardUrl = buildBusinessDashboardUrl(
        selectedBusiness.xxbID,
        instanceUrl
      );
      if (!dashboardUrl) {
        handleClose();
        return;
      }
      const result = await createSessionForExternal(dashboardUrl);
      console.log(result);
      if (!result) {
        handleOpen();
      }
    }
    handleClose();
  };

  const handleFavoriteToggle = async (business, newValue) => {
    const favoriteData = {
      DatabaseId: business.xxbID,
      FavouriteYN: newValue,
    };
    const url = `https://${envConfig.apiDev1}/api/v3/en-au/databases/manage-user-favourite-databases?BaseHostURL=${envConfig.loginUrl}`;
    const response = await fetch(url, {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify(favoriteData),
    });
    const responseData = await response.json();
    // Error handled by interceptor

    if (responseData.SuccesYN === "Y") {
      await handleFavChanged();
    }
    handleClose();
  };

  const handleOpenInNewTab = async () => {
    if (selectedBusiness) {
      if (USE_SAMPLE_DATA) {
        enqueueSnackbar(
          `Would open business ${selectedBusiness.xxbBusinessName} in new tab`,
          { variant: "info" }
        );
        handleClose();
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "SendRequest") {
        setOpenSendRequestAccess(true);
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "RequestInProgress") {
        setOpenRequestInProgress(true);
        return;
      }

      if (selectedBusiness.xxbAccessStatus === "AutoAdd") {
        setCrossDomainCookie("databaseId", selectedBusiness.xxbID);
        const result = await requestAccessToBusinessFile({
          dbId: selectedBusiness.xxbID,
          subscriberId: selectedBusiness.xxsID,
        });
        console.log(result);
        if (
          result.SuccessYN === "Y" ||
          result.successYN === "Y" ||
          result.SuccesYN === "Y"
        ) {
          window.open(buildBusinessDashboardUrl(selectedBusiness.xxbID), "_blank");
        }
        handleClose();
        return;
      }

      setCrossDomainCookie("databaseId", selectedBusiness.xxbID);
      window.open(buildBusinessDashboardUrl(selectedBusiness.xxbID), "_blank");
    }
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedBusiness(null);
  };

  return (
    <Container
      sx={{
        marginTop: 5,
        p: 1,
        pb: 3,
        position: "relative",
        boxShadow: "0px 0px 10px 0px rgba(69, 90, 100, 0.10)",
        maxWidth: fullwidth ? "unset" : "416px",
        ml: fullwidth ? 0 : "16px",
        border: theme.palette.borders.main,
        borderRadius: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: "24px",
          mt: 3,
          fontWeight: 400,
          lineHeight: "150%",
          letterSpacing: "-0.264px",
        }}
      >
        Favorites
      </Typography>
      <Grid
        sx={{ mt: 1, display: "flex", flexDirection: "column" }}
        container
        spacing="12px"
      >
        {favorites.map((business, index) => (
          <Grid
            item
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": {
                color: theme.palette.primary.main,
              },
            }}
            onClick={() => {
              handleDbSelect(
                business.xxbID,
                business.xxbAccessStatus,
                business.xxsID
              );
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              handleContextMenu(event, business);
            }}
            data-testid={`business-file-favorite-${business.xxbID}`}
          >
            <IconButton
              aria-label="more"
              aria-controls={`long-menu-${index}`}
              aria-haspopup="true"
              onClick={(event) => {
                event.stopPropagation();
                handleContextMenu(event, business);
              }}
              data-testid="dashboard--IconButton-2">
              <MoreVertIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                mx: 2,
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              {business.xxbBusinessName}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {selectedBusiness?.xxbAccessStatus === "HasAccess" && (
          <MenuItem
            onClick={() =>
              handleFavoriteToggle(
                selectedBusiness,
                selectedBusiness.xxlFavouriteBF === "Y" ? "N" : "Y"
              )
            }
            data-testid="dashboard--MenuItem-11">
            {selectedBusiness?.xxlFavouriteBF === "Y"
              ? "Remove from favorites"
              : "Add to favorites"}
          </MenuItem>
        )}
        <MenuItem onClick={handleOpenInNewTab} data-testid="dashboard--MenuItem-12">
          <OpenInNewIcon sx={{ mr: 1, fontSize: 20 }} />
          Open in new tab
        </MenuItem>
      </Menu>
    </Container>
  );
};

const Dashboard = () => {
  const secondBreakpoint = useMediaQuery("(max-width: 875px)");
  const navigate = useNavigate();
  const location = useLocation();
  const {} = useParams();

  const { isDarkMode } = useTheme();
  const theme = getAppTheme(isDarkMode);
  const [isOpen, setIsOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { handleOpen: realHandleOpen } = useLoginPopup();
  const handleOpen = USE_SAMPLE_DATA ? () => {} : realHandleOpen;

  const [isProcessingBankFeedsCallback, setIsProcessingBankFeedsCallback] =
    useState(false);
  const [, setDialogOpen] = useState(false);
  const [invitationsData, setInvitationsData] = useState(null);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [, setInvitationsError] = useState(null);

  const [businessFilesOfUserData, setBusinessFilesOfUserData] = useState(null);
  const [, setBusinessFilesOfUserError] = useState(null);
  const [businessFilesOfUserLoading, setBusinessFilesOfUserLoading] =
    useState(true);
  const [isIncognitoUser, setIsIncognitoUser] = useState(false);
  const [incognitoDialogOpen, setIncognitoDialogOpen] = useState(false);
  const [hasEnabled2FA, setHasEnabled2FA] = useState(null);

  const [openSendRequestAccess, setOpenSendRequestAccess] = useState(false);
  const [openRequestInProgress, setOpenRequestInProgress] = useState(false);

  const [currentSelectedDatabase, setCurrentSelectedDatabase] = useState(null);
  const [currentSelectedSubscriber, setCurrentSelectedSubscriber] =
    useState(null);

  const handleDialogClose = () => setDialogOpen(false);

  // Bank feeds Basiq callback: when user lands on user-dashboard with state/jobId from Basiq redirect
  // Redirect to state page first; the state page will call register-async so errors are visible
  const bankFeedsCallbackHandled = useRef(false);
  useEffect(() => {
    if (bankFeedsCallbackHandled.current || USE_SAMPLE_DATA) return;

    const queryParams = new URLSearchParams(location.search);
    const state = queryParams.get("state");
    const jobId =
      queryParams.get("jobId") ||
      (queryParams.get("jobIds") || "").split(",")[0]?.trim() ||
      "";

    if (!state) return;

    bankFeedsCallbackHandled.current = true;
    setIsProcessingBankFeedsCallback(true);

    const target =
      state.startsWith("http://") || state.startsWith("https://")
        ? state
        : `${window.location.origin}${state.startsWith("/") ? "" : "/"}${state}`;
    const url = new URL(target);
    if (jobId) url.searchParams.set("jobId", jobId);
    window.location = url.toString();
  }, [location.search]);

  const fetchInvitations = async () => {
    setInvitationsLoading(true);
    try {
      if (USE_SAMPLE_DATA) {
        // Use sample data for local development
        setTimeout(() => {
          setInvitationsData({
            ListOfInvitations: [],
            ListOfAccessRequests: [],
            SubscriberTextInfo: null,
          });
          setInvitationsLoading(false);
        }, 500);
      } else {
        const response = await fetch(
          `https://${envConfig.apiDev1}/api/v3/en-au/databases/get-pending-invitations-with-access-requests?BaseHostURL=${envConfig.loginUrl}`,
          {
            method: "GET",
            headers: getHeaders(),
            body: null,
            navigate,
            handleOpen,
          }
        );
        const data = await response.json();
        setInvitationsData(data);
        const enabled = readCurrentUser2FAEnabled(data);
        if (enabled !== null) {
          setHasEnabled2FA(enabled);
        }
      }
    } catch (error) {
      setInvitationsError(error);
    } finally {
      if (!USE_SAMPLE_DATA) {
        setInvitationsLoading(false);
      }
    }
  };

  const fetchBusinessFiles = useCallback(async () => {
    setBusinessFilesOfUserLoading(true);
    try {
      if (USE_SAMPLE_DATA) {
        // Use sample data for local development
        setTimeout(() => {
          setBusinessFilesOfUserData(SAMPLE_BUSINESS_FILES);
          setIsIncognitoUser(false);
          setBusinessFilesOfUserLoading(false);
        }, 500);
      } else {
        const response = await fetch(
          `https://${envConfig.apiDev1}/api/v5/en-au/databases/get-user-databases-async?BaseHostURL=${envConfig.loginUrl}`,
          {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({}),
          }
        );

        const result = await response.json();
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        // Debug: Log the raw API response
        console.log("=== API RESPONSE DEBUG ===");
        console.log(
          "Total files from API:",
          result?.ListOfBusinessFile?.length
        );

        // Check what date fields actually exist in the data
        if (result?.ListOfBusinessFile?.length > 0) {
          const firstFile = result.ListOfBusinessFile[0];
          console.log("First file object keys:", Object.keys(firstFile));
          console.log("First file sample:", {
            name: firstFile.xxbBusinessName,
            id: firstFile.xxbID,
            ssnLastConnnectedDate: firstFile.ssnLastConnnectedDate,
            anyOtherDateField: Object.keys(firstFile).filter((key) =>
              key.toLowerCase().includes("date")
            ),
          });

          // Group by actual date values
          const dateGroups = {};
          result.ListOfBusinessFile.forEach((file) => {
            const date = file.ssnLastConnnectedDate || "NO_DATE";
            if (!dateGroups[date]) dateGroups[date] = 0;
            dateGroups[date]++;
          });
          console.log("Date distribution in API response:", dateGroups);
        }
        console.log("=== END API RESPONSE DEBUG ===");

        setBusinessFilesOfUserData(result?.ListOfBusinessFile);
        setIsIncognitoUser(result?.IncognitoUserYN === "Y");
        const enabled = readCurrentUser2FAEnabled(result);
        if (enabled !== null) {
          setHasEnabled2FA(enabled);
        }
      }
    } catch (error) {
      setBusinessFilesOfUserError(error);
    } finally {
      if (!USE_SAMPLE_DATA) {
        setBusinessFilesOfUserLoading(false);
      }
    }
  }, []);

  const requestAccessToBusinessFile = async ({ dbId, subscriberId }) => {
    const url = `https://${envConfig.apiDev1}/api/v3/en-au/databases/request-access-to-business-file-async?BaseHostURL=${envConfig.loginUrl}`;
    const response = await fetch(url, {
      method: "POST",
      headers: getHeaders(String(dbId)),
      body: JSON.stringify({
        DatabaseId_xxbId: dbId,
        SubscriberId_xxsId: subscriberId,
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const result = await response.json();
    return result;
  };

  useEffect(() => {
    fetchInvitations();
    fetchBusinessFiles();
  }, []);

  const invitations = invitationsData?.ListOfInvitations;
  const requestedAccess = invitationsData?.ListOfAccessRequests;

  const firstName = USE_SAMPLE_DATA ? "Demo User" : getCookie("firstName");

  const favorites = (businessFilesOfUserData || [])?.filter(
    (business) => business?.xxlFavouriteBF === "Y"
  );

  const handleIncognitoWarning = () => {
    setIncognitoDialogOpen(true);
  };

  const handleDbSelect = async (dbId, accessStatus, subscriberId) => {
    setCurrentSelectedDatabase(dbId);
    setCurrentSelectedSubscriber(subscriberId);
    if (USE_SAMPLE_DATA) {
      // For sample data, just log the selection
      console.log(`Selected business: ${dbId}`);
      enqueueSnackbar(`Selected business ID: ${dbId}`, { variant: "info" });
      return;
    }
    if (accessStatus === "HasAccess") {
      setCrossDomainCookie("databaseId", dbId);
      const dashboardUrl = buildBusinessDashboardUrl(dbId);
      if (!dashboardUrl) {
        return;
      }
      const result = await createSessionForExternal(dashboardUrl);
      console.log(result);
      if (!result) {
        handleOpen();
      }
    }

    if (accessStatus === "AutoAdd") {
      setCrossDomainCookie("databaseId", dbId);
      // TODO: double check if this is the correct subscriberId
      const result = await requestAccessToBusinessFile({
        dbId,
        subscriberId,
      });
      console.log(result);
      if (
        result.SuccessYN === "Y" ||
        result.successYN === "Y" ||
        result.SuccesYN === "Y"
      ) {
        const dashboardUrl = buildBusinessDashboardUrl(dbId);
        if (!dashboardUrl) {
          return;
        }
        const result = await createSessionForExternal(dashboardUrl);
        console.log(result);
        if (!result) {
          handleOpen();
        }
      }
      return;
    }

    if (accessStatus === "SendRequest") {
      setOpenSendRequestAccess(true);
      return;
    }

    if (accessStatus === "RequestInProgress") {
      setOpenRequestInProgress(true);
      return;
    }
  };

  if (isProcessingBankFeedsCallback) {
    return <BackdropLoading open={true} />;
  }

  if (invitationsLoading) {
    return (
      <BackdropLoading
        open={invitationsLoading || businessFilesOfUserLoading}
      />
    );
  }

  return (
    <>
      <BackdropLoading
        open={invitationsLoading || businessFilesOfUserLoading}
      />
      <Dialog
        open={openSendRequestAccess}
        onClose={() => setOpenSendRequestAccess(false)}
        data-testid="dashboard--Dialog-0">
        <DialogTitle data-testid="dashboard--DialogTitle-0">Request Access</DialogTitle>
        <DialogContent data-testid="dashboard--DialogContent-0">
          <DialogContentText>
            Admin of the file must approve your access first. Do you want to
            request this access?
          </DialogContentText>
          <DialogActions data-testid="dashboard--DialogActions-0">
            <Button
              onClick={() => setOpenSendRequestAccess(false)}
              data-testid="dashboard--Button-8">No</Button>
            <Button
              onClick={async () => {
                await requestAccessToBusinessFile({
                  dbId: currentSelectedDatabase,
                  subscriberId: currentSelectedSubscriber,
                });
                await fetchBusinessFiles();
                setOpenSendRequestAccess(false);
              }}
              data-testid="dashboard--Button-9">
              Yes
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openRequestInProgress}
        onClose={() => setOpenRequestInProgress(false)}
        data-testid="dashboard--Dialog-1">
        <DialogTitle data-testid="dashboard--DialogTitle-1">Request Access in Progress</DialogTitle>
        <DialogContent data-testid="dashboard--DialogContent-1">
          <DialogContentText>
            Your request is in progress but has not been actioned yet. Contact
            admin of the file to approve your request.
          </DialogContentText>
          <DialogActions data-testid="dashboard--DialogActions-1">
            <Button
              onClick={() => setOpenRequestInProgress(false)}
              data-testid="dashboard--Button-10">
              Close
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      {(invitations?.length > 0 ||
        requestedAccess?.length > 0 ||
        businessFilesOfUserData?.length > 0 ||
        USE_NEW_DASHBOARD_DEFAULT) && (
        <>
          <Box sx={{ mt: 4, mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 2, sm: 0 } }}>
            <Typography
              sx={{
                ...commonThemes.pageTitle,
                color: theme.palette.text.secondary,
                marginBottom: 0,
              }}
            >
              {`Welcome to Exacc${
                USE_SAMPLE_DATA
                  ? "!"
                  : firstName && firstName !== "undefined"
                  ? `, ${firstName}!`
                  : "!"
              }`}
            </Typography>
            {!USE_SAMPLE_DATA && hasEnabled2FA === false && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/mfa-setup")}
                data-testid="dashboard--Button-11">
                Configure 2FA
              </Button>
            )}
          </Box>
        </>
      )}
      {requestedAccess?.length > 0 && (
        <AccessRequestsDashboard
          requestedAccess={requestedAccess}
          refetchInvitations={fetchInvitations}
          fetchBusinessFiles={fetchBusinessFiles}
        />
      )}
      {invitations?.length > 0 && (
        <InvitationsDashboard
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          invitations={invitations}
          refetchInvitations={fetchInvitations}
          fetchBusinessFiles={fetchBusinessFiles}
        />
      )}
      {(businessFilesOfUserData?.length > 0 || USE_NEW_DASHBOARD_DEFAULT) &&
        (USE_NEW_DASHBOARD_DEFAULT && businessFilesOfUserData?.length <= 7 ? (
          // Use card layout when new dashboard is enabled AND 7 or fewer files
          (<BusinessFilesDashboard
            subscriberTextInfo={invitationsData?.SubscriberTextInfo}
            fullwidth={true}
            files={businessFilesOfUserData || []}
            handleDbSelect={handleDbSelect}
            handleFavChanged={fetchBusinessFiles}
            setOpenSendRequestAccess={setOpenSendRequestAccess}
            setOpenRequestInProgress={setOpenRequestInProgress}
            requestAccessToBusinessFile={requestAccessToBusinessFile}
            setCurrentSelectedDatabase={setCurrentSelectedDatabase}
            setCurrentSelectedSubscriber={setCurrentSelectedSubscriber}
            isIncognitoUser={isIncognitoUser}
            onIncognitoWarning={handleIncognitoWarning}
          />)
        ) : (
          // Use original layout with favorites for more than 7 files
          (<Box
            sx={{
              display: "flex",
              flexDirection: secondBreakpoint ? "column" : "row-reverse",
            }}
          >
            {favorites?.length > 0 && (
              <FavoritesDashboard
                fullwidth={secondBreakpoint}
                favorites={favorites}
                handleDbSelect={handleDbSelect}
                handleFavChanged={fetchBusinessFiles}
                setOpenSendRequestAccess={setOpenSendRequestAccess}
                setOpenRequestInProgress={setOpenRequestInProgress}
                requestAccessToBusinessFile={requestAccessToBusinessFile}
                setCurrentSelectedDatabase={setCurrentSelectedDatabase}
                setCurrentSelectedSubscriber={setCurrentSelectedSubscriber}
              />
            )}
            <BusinessFilesDashboard
              subscriberTextInfo={invitationsData?.SubscriberTextInfo}
              fullwidth={secondBreakpoint}
              files={businessFilesOfUserData || []}
              handleDbSelect={handleDbSelect}
              handleFavChanged={fetchBusinessFiles}
              setOpenSendRequestAccess={setOpenSendRequestAccess}
              setOpenRequestInProgress={setOpenRequestInProgress}
              requestAccessToBusinessFile={requestAccessToBusinessFile}
              setCurrentSelectedDatabase={setCurrentSelectedDatabase}
              setCurrentSelectedSubscriber={setCurrentSelectedSubscriber}
              isIncognitoUser={isIncognitoUser}
              onIncognitoWarning={handleIncognitoWarning}
            />
          </Box>)
        ))}
      {!invitations?.length > 0 &&
        !requestedAccess?.length > 0 &&
        !businessFilesOfUserData?.length > 0 &&
        !USE_NEW_DASHBOARD_DEFAULT && (
          <EmptyDashboard
            isIncognitoUser={isIncognitoUser}
            onIncognitoWarning={handleIncognitoWarning}
          />
        )}
      <Dialog
        open={incognitoDialogOpen}
        onClose={() => setIncognitoDialogOpen(false)}
        data-testid="dashboard--Dialog-2">
        <DialogTitle data-testid="dashboard--DialogTitle-2">Incognito Access</DialogTitle>
        <DialogContent data-testid="dashboard--DialogContent-2">
          <DialogContentText>
            You cannot add live files as you are using incognito access.
          </DialogContentText>
          <DialogActions data-testid="dashboard--DialogActions-2">
            <Button
              onClick={() => setIncognitoDialogOpen(false)}
              data-testid="dashboard--Button-12">OK</Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Dashboard;
